import { Box, Flex, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Banner, OutlineCard } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { BoardMemberDetails } from '../Submission/AuthorsTab/AuthorSummary';
import ProfileDetails from './ProfileDetails';
import ChangePassword from './ChangePassword';
import ResearchFocusAndExpertiseForm from './ResearchFocusAndExpertise';
import { fetchUserProfileData } from 'src/api/signUpMutation';
import RinggoldBadge from '../RinggoldBadge';
import UpdateAffiliationDetails from './UpdateAffiliationDetails';
import { IUserType } from '../UserRegistrationSteps/StepThree/signupHook';
import { convertDataToLabel } from 'src/pages/MyTask/Assessments/hooks';
import { formatDateAndTime } from 'src/utils/date';
import { OrcidIDIcon } from './RenderSectionDetails';
import UpdateAdditionalAffiliation from './AdditionalAffiliation/UpdateAdditionalAffiliation';
import Footer from '../Footer';
import { IAffiliationDetails } from '@publishing-engine/domain/models';

const AuthorInfo = ({
  user,
  affiliationDetails
}: {
  user: IUserType;
  affiliationDetails: IAffiliationDetails;
}) => {
  const {
    firstName = '',
    lastName = '',
    email,
    orcid,
    prefix,
    middleName = '',
    lastLoginAt
  } = user;

  return (
    <Flex direction="column" width="100%" gap={1}>
      <Flex gap={1}>
        <Text textStyle="headingGroup">
          {prefix && convertDataToLabel(prefix)}
        </Text>
        <Text textStyle="headingGroup">{`${firstName} ${
          middleName && middleName
        } ${lastName}`}</Text>
      </Flex>

      <Text textStyle="textBody">
        {affiliationDetails?.ringgoldId && <RinggoldBadge />}{' '}
        {affiliationDetails?.organizationName}
      </Text>

      {orcid && (
        <Flex textStyle="textBody" gap={1}>
          <OrcidIDIcon value={orcid} />
          {orcid}
        </Flex>
      )}

      {email && (
        <Text textStyle="textBody">
          <FontAwesomeIcon icon={faEnvelope} /> {email}
        </Text>
      )}

      {lastLoginAt && (
        <Text textStyle="textBody">
          Last login: {formatDateAndTime(lastLoginAt)}
        </Text>
      )}
    </Flex>
  );
};

const UserProfile = () => {
  const { data } = useQuery(['user-profile-data'], fetchUserProfileData);

  const authorData = data?.data;
  if (!authorData) return null;

  const {
    user,
    affiliationDetails,
    profileDetails,
    researchFocusAndExpertise,
    boardMemberships = [],
    organizationId,
    additionalAffiliations
  } = authorData;

  const isDataMissing =
    !user.prefix ||
    !user.firstName ||
    !user.lastName ||
    !user.email ||
    !Object.keys(affiliationDetails || {}).length ||
    !researchFocusAndExpertise?.reviewerInterest ||
    (researchFocusAndExpertise?.reviewerInterest === 'yes' &&
      (!researchFocusAndExpertise?.unavailabilityPeriodFrom ||
        !researchFocusAndExpertise?.unavailabilityPeriodTo));

  return (
    <Flex direction="column">
      <Flex direction="column" w="100%" minHeight="100%" padding={5} gap={3}>
        <Flex align={'center'} justifyContent={'space-between'}>
          <Text textStyle="headingSection">My Account</Text>
          <ChangePassword />
        </Flex>
        <OutlineCard width="100%" padding={4}>
          <AuthorInfo user={user} affiliationDetails={affiliationDetails} />
        </OutlineCard>

        {isDataMissing && (
          <Box my={3}>
            <Banner
              customIcon={<FontAwesomeIcon icon={faWarning} />}
              variant="attention"
              heading="Add Missing Information"
              content={
                <Flex direction={'column'} gap={3}>
                  <Text>
                    Completing your profile makes it easier for publishers to
                    understand and connect with your work.
                  </Text>
                </Flex>
              }
            />
          </Box>
        )}

        <Flex direction="column" gap={6}>
          <ProfileDetails
            organizationId={organizationId || ''}
            data={{ user, profileDetails }}
          />
          <UpdateAffiliationDetails
            affiliation={affiliationDetails}
            organizationId={organizationId || ''}
          />
          <UpdateAdditionalAffiliation
            data={{ additionalAffiliations, affiliationDetails }}
            organizationId={organizationId || ''}
          />
          <ResearchFocusAndExpertiseForm
            organizationId={organizationId || ''}
            data={{ researchFocusAndExpertise }}
          />
          <BoardMemberDetails boardMemberships={boardMemberships} />
        </Flex>
      </Flex>
      <Footer />
    </Flex>
  );
};

export default UserProfile;
